import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import AppIconImage from '../AppIconImage';
import ImageAssetUrlInput, { ImageUploader } from '../ImageAssetUrlInput';
import { tenantApi } from '../../lib/tenant-api';
import { getExpertErrorMessage, isRequestCancelled } from '../../lib/domain-expert-api';
import { IconConversionError, MAX_ICON_PNG_DIMENSION, isUsableIconPng, toIconPngFile } from '../../lib/imageToIconPng';
import { httpUrlOrNull } from '../../lib/string.lib';
import { useDebouncedValue } from './ui';

// client-mgmt-api's limit for app_icon_url.
const MAX_URL_LENGTH = 2048;
const FETCH_TIMEOUT_MS = 15_000;
// Image types other than PNG, by extension; asset links end in their file's own.
const NON_PNG_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'jpe', 'jfif', 'pjpeg', 'webp', 'gif', 'svg', 'svgz', 'avif', 'bmp', 'ico', 'tif', 'tiff', 'heic', 'heif', 'jxl',
]);

const NOT_CONVERTED =
  "This image isn't a PNG and can't be converted from its link, so the installed app will show the letter icon. Upload the image file instead: uploads are converted to PNG.";

const LINK_STEP_TEXT = {
  checking: 'Checking this image…',
  converting: 'Converting this image to PNG…',
  uploading: 'Uploading the converted PNG…',
} as const;

const linkExtension = (url: string) => {
  try {
    return /\.([a-z0-9]+)$/i.exec(new URL(url).pathname)?.[1].toLowerCase() ?? null;
  } catch {
    return null;
  }
};

const isNonPngLink = (url: string) => NON_PNG_EXTENSIONS.has(linkExtension(url) ?? '');

// The link's file name, to name its converted copy after.
const linkFileName = (url: string) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() ?? '');
  } catch {
    return '';
  }
};

// The image's bytes, or null when this page may not read them. Showing an image needs no
// permission, but reading its bytes needs the host to allow it (CORS); the asset storage doesn't.
const fetchImage = async (url: string, signal: AbortSignal): Promise<Blob | null> => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort);
  const timer = setTimeout(abort, FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store', signal: controller.signal });
    return response.ok ? await response.blob() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  }
};

type Step = 'checking' | 'converting' | 'uploading';

interface ImageUploadFieldProps {
  id: string;
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
  placeholderColor: string;
  placeholderText: string;
}

export default function ImageUploadField({ id, value, onChange, disabled, placeholderColor, placeholderText }: ImageUploadFieldProps) {
  const [error, setError] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{ url: string; width: number; height: number } | null>(null);
  // An uploaded file while it's converted and uploaded: shown in the tile as the admin picked it.
  const [upload, setUpload] = useState<{ preview: string; step: Exclude<Step, 'checking'> } | null>(null);
  // The link the admin last typed or picked; one loaded with the page or reset isn't converted.
  const [chosen, setChosen] = useState<string | null>(null);
  // A chosen link being converted, and what checking it found (message null: usable as it is).
  const [linkTask, setLinkTask] = useState<{ url: string; step: Step } | null>(null);
  const [linkResult, setLinkResult] = useState<{ url: string; message: string | null } | null>(null);
  // A link this field filled in itself shows at once rather than after the typing pause.
  const [applied, setApplied] = useState<string | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  // A typed link is loaded once typing pauses; a cleared field shows at once.
  const settled = useDebouncedValue(value);
  const shown = value.trim() ? (value === applied ? value : settled).trim() : '';
  const previewUrl = httpUrlOrNull(shown);

  // A chosen link that isn't a PNG is converted when its host lets this page read the image:
  // the PNG is uploaded to Assets and replaces the link. A PNG link is used as it is.
  useEffect(() => {
    const url = previewUrl;
    if (!url || chosen?.trim() !== url || !/^https:\/\//i.test(url) || linkExtension(url) === 'png') return;
    const controller = new AbortController();
    const live = () => !controller.signal.aborted;
    const stop = (message?: string | null) => {
      if (!live()) return;
      setLinkTask(null);
      if (message !== undefined) setLinkResult({ url, message });
    };

    (async () => {
      setLinkResult(null);
      setLinkTask({ url, step: 'checking' });
      const bytes = await fetchImage(url, controller.signal);
      // Unreadable: a non-PNG link keeps its notice; any other may be a PNG, so says nothing.
      if (!bytes) return stop();
      if (await isUsableIconPng(bytes)) return stop(null);

      if (!live()) return;
      setLinkTask({ url, step: 'converting' });
      let png: File;
      try {
        png = await toIconPngFile(bytes, linkFileName(url));
      } catch (err) {
        const reason = err instanceof IconConversionError ? err.message : "This image can't be converted.";
        return stop(`${reason} The installed app needs it as a PNG, so choose another image.`);
      }
      if (!live()) return;
      setLinkTask({ url, step: 'uploading' });
      try {
        const asset = await tenantApi.uploadAsset(png);
        const uploaded = asset?.custom_metadata?.public_url;
        if (!uploaded) throw new Error('missing url');
        if (live() && valueRef.current.trim() === url) {
          setApplied(uploaded);
          onChangeRef.current(uploaded);
        }
        stop();
      } catch (err) {
        stop();
        if (live() && !isRequestCancelled(err)) {
          setError(getExpertErrorMessage(err, "We couldn't upload the converted image. Please try again."));
        }
      }
    })();

    return () => {
      controller.abort();
      setLinkTask(null);
    };
  }, [previewUrl, chosen]);

  const tileUrl = upload?.preview ?? previewUrl;
  const showImage = tileUrl !== null && tileUrl !== failedUrl;
  const size = loaded && loaded.url === previewUrl ? loaded : null;
  const linkStep = linkTask && linkTask.url === previewUrl ? linkTask.step : null;
  const checked = linkResult && linkResult.url === previewUrl ? linkResult : null;
  let notice: string | null = null;
  if (shown && !/^https:\/\//i.test(shown)) notice = 'Use a link that starts with https://.';
  else if (previewUrl !== null && previewUrl === failedUrl) notice = "We couldn't load an image from this link.";
  else if (previewUrl === null || linkStep) notice = null;
  else if (checked) notice = checked.message;
  else if (isNonPngLink(previewUrl)) notice = NOT_CONVERTED;
  else if (size && Math.max(size.width, size.height) > MAX_ICON_PNG_DIMENSION) {
    notice = `This image is ${size.width} × ${size.height} pixels, more than the installed app can use (${MAX_ICON_PNG_DIMENSION} × ${MAX_ICON_PNG_DIMENSION}), so it will show the letter icon. Upload the image file instead: uploads are converted to fit.`;
  }

  const uploadIcon: ImageUploader = async (file, apply) => {
    setError(null);
    // The file is the newer choice, so a link still being converted stops rather than replacing
    // it. If the file isn't used, that link is converted again, unless something else was chosen.
    const pendingLink = chosen;
    setChosen(null);
    let used = false;
    const preview = URL.createObjectURL(file);
    setUpload({ preview, step: 'converting' });
    try {
      let png: File;
      try {
        png = await toIconPngFile(file, file.name);
      } catch (err) {
        setError(`${err instanceof IconConversionError ? err.message : "We couldn't convert this image."} Try another file.`);
        return;
      }
      setUpload({ preview, step: 'uploading' });
      try {
        const asset = await tenantApi.uploadAsset(png);
        const url = asset?.custom_metadata?.public_url;
        if (!url) throw new Error('missing url');
        used = apply(url);
        if (used) setApplied(url);
      } catch (err) {
        if (!isRequestCancelled(err)) setError(getExpertErrorMessage(err, "We couldn't upload this image. Please try again."));
      }
    } finally {
      if (!used) setChosen((current) => current ?? pendingLink);
      setUpload(null);
      URL.revokeObjectURL(preview);
    }
  };

  const handleChange = (url: string) => {
    setError(null);
    setChosen(url);
    onChange(url);
  };

  return (
    <div className="flex items-start gap-4">
      {showImage ? (
        <AppIconImage
          src={tileUrl}
          alt="App icon"
          onLoad={(e) => setLoaded({ url: tileUrl, width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
          onError={() => setFailedUrl(tileUrl)}
          className="h-16 w-16 flex-shrink-0"
        />
      ) : (
        <div
          className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl text-xl font-semibold text-white"
          style={{ backgroundColor: placeholderColor }}
          aria-hidden="true"
        >
          {placeholderText}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <ImageAssetUrlInput
          id={id}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder="https://example.com/icon.png"
          maxLength={MAX_URL_LENGTH}
          uploadFile={uploadIcon}
          uploadingLabel={upload?.step === 'converting' ? 'Converting…' : 'Uploading…'}
          asAppIcons
        />
        <div className="flex items-start justify-end gap-3">
          {value && (
            <button
              type="button"
              onClick={() => handleChange('')}
              disabled={disabled}
              className="mt-1 flex-shrink-0 text-xs font-medium text-gray-600 hover:text-red-600 disabled:opacity-50"
            >
              Remove
            </button>
          )}
        </div>
        {error ? (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : linkStep ? (
          <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {LINK_STEP_TEXT[linkStep]}
          </p>
        ) : (
          // While a file uploads the tile shows it, not the link, so the link's notice waits.
          notice && !upload && <p className="mt-1 text-sm text-amber-700">{notice}</p>
        )}
      </div>
    </div>
  );
}
